/* eslint-disable no-param-reassign */
import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { ThemeMode } from '../../theme/themes';

export type UserSettingsState = {
    orderColumnFilters: string[];
    settlementColumnFilters: string[];
    logColumnFilters: string[];
    error: string;
    flow?: string;
    exactSearch?: boolean;
    aiSearchEnabled?: boolean;
    perPage?: string;
    tableExpanded?: boolean;
    newVersionAvailable: boolean;
    orderAccountSearchTab?: string;
    alertMessage?: string;
    themeMode: ThemeMode;
};

const initialState: UserSettingsState = {
    orderColumnFilters: [],
    settlementColumnFilters: [],
    logColumnFilters: [],
    error: '',
    flow: '',
    exactSearch: false,
    aiSearchEnabled: false,
    perPage: '',
    tableExpanded: false,
    newVersionAvailable: false,
    orderAccountSearchTab: undefined,
    alertMessage: undefined,
    themeMode: 'light',
};

export const userSettingsSlice = createSlice({
    name: 'userSettings',
    initialState,
    reducers: {
        saveColumnFiltersRequested: state => {},
        saveColumnFiltersSucceeded: (
            state,
            action: PayloadAction<{
                tableHeaderType: string;
                columnFilters: string[];
            }>
        ) => {
            const newState = {
                ...current(state),
                [action.payload.tableHeaderType]: action.payload.columnFilters,
            };

            return newState;
        },
        saveColumnFiltersFailed: state => {
            state.error = '';
        },
        setUserFlow: (state, action: PayloadAction<string>) => {
            state.flow = action.payload;
        },
        saveExactSearch: (state, action: PayloadAction<boolean>) => {
            state.exactSearch = action.payload;
        },
        setPerPage: (state, action: PayloadAction<string>) => {
            state.perPage = action.payload;
        },
        setTableExpanded: (state, action: PayloadAction<boolean>) => {
            state.tableExpanded = action.payload;
        },
        setNewVersionAvailable: (state, action: PayloadAction<boolean>) => {
            state.newVersionAvailable = action.payload;
        },
        setOrderAccountSearchTab: (state, action: PayloadAction<string>) => {
            state.orderAccountSearchTab = action.payload;
        },
        setAiSearchEnabled: (state, action: PayloadAction<boolean>) => {
            state.aiSearchEnabled = action.payload;
        },
        setAlertMessage: (state, action: PayloadAction<string | undefined>) => {
            state.alertMessage = action.payload;
        },
        setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
            state.themeMode = action.payload;
            if (typeof window !== 'undefined') {
                localStorage.setItem('themeMode', action.payload);
            }
        },
        toggleThemeMode: state => {
            const newMode = state.themeMode === 'light' ? 'dark' : 'light';
            state.themeMode = newMode;
            if (typeof window !== 'undefined') {
                localStorage.setItem('themeMode', newMode);
            }
        },
    },
});

export const {
    saveColumnFiltersRequested,
    saveColumnFiltersSucceeded,
    saveColumnFiltersFailed,
    setUserFlow,
    saveExactSearch,
    setAiSearchEnabled,
    setPerPage,
    setTableExpanded,
    setNewVersionAvailable,
    setOrderAccountSearchTab,
    setAlertMessage,
    setThemeMode,
    toggleThemeMode,
} = userSettingsSlice.actions;

export default userSettingsSlice.reducer;
